import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Form, Button, Alert, Card } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../services/supabase';
import '../styles/AdminLogin.css';

const AdminLogin = () => {
  const [credentials, setCredentials] = useState({
    email: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [loginAttempts, setLoginAttempts] = useState(0);
  const [isLocked, setIsLocked] = useState(false);
  const navigate = useNavigate();

  const MAX_LOGIN_ATTEMPTS = 5;
  const LOCKOUT_DURATION = 15 * 60 * 1000; // 15 minutes

  useEffect(() => {
    const lockoutTime = localStorage.getItem('adminLockoutTime');
    const attempts = parseInt(localStorage.getItem('adminLoginAttempts') || '0');

    if (lockoutTime && attempts >= MAX_LOGIN_ATTEMPTS) {
      const timeLeft = parseInt(lockoutTime) + LOCKOUT_DURATION - Date.now();
      if (timeLeft > 0) {
        setIsLocked(true);
        setLoginAttempts(attempts);
        setTimeout(() => {
          setIsLocked(false);
          setLoginAttempts(0);
          localStorage.removeItem('adminLockoutTime');
          localStorage.removeItem('adminLoginAttempts');
        }, timeLeft);
      } else {
        localStorage.removeItem('adminLockoutTime');
        localStorage.removeItem('adminLoginAttempts');
      }
    } else {
      setLoginAttempts(attempts);
    }
  }, [MAX_LOGIN_ATTEMPTS, LOCKOUT_DURATION]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCredentials(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isLocked) {
      setError('Account is temporarily locked due to too many failed attempts. Please try again later.');
      return;
    }

    setIsLoading(true);
    setError('');

    await new Promise((r) => setTimeout(r, 500));

    if (!supabase) {
      // Fallback: local-only dev credentials (data stays in localStorage)
      const DEV_ADMIN = { email: 'admin@skyline-rp.org', password: 'Shoresy26' };
      if (credentials.email.trim().toLowerCase() === DEV_ADMIN.email && credentials.password === DEV_ADMIN.password) {
        localStorage.setItem('adminLoggedIn', 'true');
        localStorage.setItem('adminLoginTime', new Date().toISOString());
        localStorage.removeItem('adminLoginAttempts');
        localStorage.removeItem('adminLockoutTime');
        navigate('/admin/dashboard');
      } else {
        const newAttempts = loginAttempts + 1;
        setLoginAttempts(newAttempts);
        localStorage.setItem('adminLoginAttempts', newAttempts.toString());
        if (newAttempts >= MAX_LOGIN_ATTEMPTS) {
          setIsLocked(true);
          localStorage.setItem('adminLockoutTime', Date.now().toString());
          setError('Too many failed attempts. Account locked for 15 minutes.');
          setTimeout(() => { setIsLocked(false); setLoginAttempts(0); localStorage.removeItem('adminLockoutTime'); localStorage.removeItem('adminLoginAttempts'); }, LOCKOUT_DURATION);
        } else {
          setError(`Invalid email or password. ${MAX_LOGIN_ATTEMPTS - newAttempts} attempts remaining.`);
        }
      }
      setIsLoading(false);
      return;
    }

    const { data, error: authError } = await supabase.auth.signInWithPassword({
      email: credentials.email.trim(),
      password: credentials.password
    });

    if (authError) {
      const newAttempts = loginAttempts + 1;
      setLoginAttempts(newAttempts);
      localStorage.setItem('adminLoginAttempts', newAttempts.toString());

      if (newAttempts >= MAX_LOGIN_ATTEMPTS) {
        setIsLocked(true);
        localStorage.setItem('adminLockoutTime', Date.now().toString());
        setError('Too many failed attempts. Account locked for 15 minutes.');
        setTimeout(() => {
          setIsLocked(false);
          setLoginAttempts(0);
          localStorage.removeItem('adminLockoutTime');
          localStorage.removeItem('adminLoginAttempts');
        }, LOCKOUT_DURATION);
      } else {
        const remaining = MAX_LOGIN_ATTEMPTS - newAttempts;
        setError(`Invalid email or password. ${remaining} attempts remaining.`);
      }
      setIsLoading(false);
      return;
    }

    if (data?.session) {
      localStorage.removeItem('adminLoginAttempts');
      localStorage.removeItem('adminLockoutTime');
      navigate('/admin/dashboard');
    }

    setIsLoading(false);
  };

  return (
    <div className="admin-login-page">
      <Container className="mt-5">
        <Row className="justify-content-center">
          <Col md={6} lg={4}>
            <Card className="admin-login-card">
              <Card.Body className="p-4">
                <div className="text-center mb-4">
                  <h2 className="admin-login-title">
                    <i className="fas fa-shield-alt me-2"></i>
                    Admin Login
                  </h2>
                </div>

                {error && (
                  <Alert variant="danger" dismissible onClose={() => setError('')}>
                    {error}
                  </Alert>
                )}

                <Form onSubmit={handleSubmit}>
                  <Form.Group className="mb-3">
                    <Form.Label>Email</Form.Label>
                    <Form.Control
                      type="email"
                      name="email"
                      value={credentials.email}
                      onChange={handleInputChange}
                      placeholder="admin@yourdomain.com"
                      required
                    />
                  </Form.Group>

                  <Form.Group className="mb-4">
                    <Form.Label>Password</Form.Label>
                    <Form.Control
                      type="password"
                      name="password"
                      value={credentials.password}
                      onChange={handleInputChange}
                      required
                    />
                  </Form.Group>

                  <Button 
                    type="submit" 
                    variant="primary" 
                    className="w-100"
                    disabled={isLoading || isLocked}
                  >
                    {isLoading ? (
                      <>
                        <i className="fas fa-spinner fa-spin me-2"></i>
                        Logging in...
                      </>
                    ) : isLocked ? (
                      <>
                        <i className="fas fa-lock me-2"></i>
                        Account Locked
                      </>
                    ) : (
                      <>
                        <i className="fas fa-sign-in-alt me-2"></i>
                        Login
                      </>
                    )}
                  </Button>
                </Form>

                <div className="text-center mt-3">
                  {loginAttempts > 0 && !isLocked && (
                    <div className="mt-2">
                      <small className="text-warning">
                        <i className="fas fa-exclamation-triangle me-1"></i>
                        Failed attempts: {loginAttempts}/{MAX_LOGIN_ATTEMPTS}
                      </small>
                    </div>
                  )}
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default AdminLogin; 